import React, { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Users, ClipboardCheck, CalendarClock, BarChart3, Clock } from "lucide-react";

const POSITION_LABEL = {
  bartender: "Bartender", server: "Server", kitchen: "Kitchen",
  host: "Host", security: "Security", manager: "Manager",
  cashier: "Cashier", runner: "Runner", promoter: "Promoter", other: "Other",
};

export default function HRLandingPage() {
  const { branchId } = useAuth();
  const [stats, setStats] = useState(null);
  const [onClock, setOnClock] = useState([]);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    if (!branchId) return;
    try {
      const [s, oc, ev] = await Promise.all([
        api.get("/hr/stats", { params: { branch_id: branchId } }),
        api.get("/attendance/on-clock", { params: { branch_id: branchId } }),
        api.get("/attendance/events", { params: { branch_id: branchId, limit: 8 } }),
      ]);
      setStats(s.data); setOnClock(oc.data); setRecent(ev.data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [branchId]);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="hr-landing">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">HR &amp; Payroll · Live</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Staff on the floor</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Roster, weekly schedule and clock events feed one honest source of truth. Hours flow into payroll prep and staff commissions once Phase 9 ships.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">On the clock right now</div>
          <div className="mt-3 display-heading text-6xl ink mono" data-testid="hr-on-clock-count">{stats?.on_clock ?? 0}</div>
          <div className="mt-1 tiny-label ink-subtle">of {stats?.employees_active ?? 0} active staff</div>
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first — schedules are per-branch.
        </div>
      )}

      {branchId && (
        <>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 animate-fade-up animate-fade-up-1">
            <Stat num=".01" label="Roster" value={stats?.employees_total ?? 0}/>
            <Stat num=".02" label="Shifts today" value={stats?.shifts_today ?? 0}/>
            <Stat num=".03" label="Published" value={stats?.published_today ?? 0} accent/>
            <Stat num=".04" label="Clocked in" value={stats?.on_clock ?? 0}/>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 animate-fade-up animate-fade-up-2">
            <div>
              <div className="section-eyebrow mb-4 flex items-center justify-between">
                <span>On the clock</span>
                <Link to="/payroll/attendance" className="tiny-label ink-subtle hover:ink">Attendance board →</Link>
              </div>
              {onClock.length === 0 ? (
                <div className="ink-subtle text-sm">No one is clocked in right now.</div>
              ) : (
                <div className="divide-y hairline-soft border-y hairline-soft" data-testid="hr-on-clock-list">
                  {onClock.map((o) => (
                    <div key={o.employee_id} className="py-3 flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[10px] uppercase tracking-widest font-bold ${
                        o.state === "on_break" ? "bg-amber-500/20 text-amber-500" : "bg-foreground text-background"
                      }`}>{o.code.slice(-3)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{o.name}</div>
                        <div className="text-xs ink-subtle mt-0.5">
                          {POSITION_LABEL[o.position] || o.position} · since {new Date(o.since).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${
                        o.state === "on_break" ? "text-amber-500 border-amber-500/40" : "text-emerald-500 border-emerald-500/40"
                      }`}>{o.state === "on_break" ? "Break" : "On clock"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="section-eyebrow mb-4 flex items-center justify-between">
                <span>Recent clock activity</span>
                <Link to="/payroll/attendance" className="tiny-label ink-subtle hover:ink">Full log →</Link>
              </div>
              {recent.length === 0 ? (
                <div className="ink-subtle text-sm">No attendance events yet.</div>
              ) : (
                <div className="divide-y hairline-soft border-y hairline-soft" data-testid="hr-recent-list">
                  {recent.map((e) => (
                    <div key={e.id} className="py-3 flex items-center gap-3">
                      <Clock size={12} className="ink-subtle shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">
                          {e.employee_name} <span className="ink-subtle font-normal">— {e.type.replace("_"," ")}</span>
                        </div>
                        <div className="text-xs ink-subtle mono mt-0.5">{new Date(e.at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })} · {e.source}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-up animate-fade-up-3">
            <NavTile to="/payroll/employees" icon={Users} label="Employees" desc="Roster · CRUD"/>
            <NavTile to="/payroll/schedules" icon={CalendarClock} label="Schedule" desc="Weekly grid"/>
            <NavTile to="/payroll/attendance" icon={ClipboardCheck} label="Attendance" desc="Live clocks + timesheet"/>
            <NavTile to="/payroll/periods" icon={BarChart3} label="Payroll" desc="Prepare · finalize · pay"/>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ num, label, value, accent }) {
  return (
    <div data-testid={`hr-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}

function NavTile({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} data-testid={`hr-nav-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}
          className="border hairline surface-card rounded-lg p-4 hover:border-foreground/50 transition-colors">
      <Icon size={18} className="ink"/>
      <div className="mt-3 text-[11px] uppercase tracking-[0.24em] font-bold ink">{label}</div>
      <div className="mt-1 text-xs ink-subtle">{desc}</div>
    </Link>
  );
}
